export type Sport = 'Pádel' | 'Tenis' | 'Pickleball';
export type EventType = 'tournament' | 'league';
export type TournamentFormat = 'round-robin' | 'eliminacion-directa' | 'sumatoria-juegos';
export type LeagueFormat = 'round-robin' | 'sumatoria-juegos' | 'escalera-piramidal';
export type Modality = 'singles' | 'doubles' | 'ambos';
export type Category = 'masculina' | 'femenina' | 'mixta';

/**
 * Get available modalities for a sport based on event type and format
 */
export const getAvailableModalities = (
  sport: Sport,
  eventType: EventType,
  format?: TournamentFormat | LeagueFormat
): Modality[] => {
  if (sport === 'Pádel') {
    if (eventType === 'tournament') {
      // Torneos de pádel solo pueden ser dobles
      return ['doubles'];
    } else if (eventType === 'league') {
      if (format === 'round-robin') {
        // Liga Round Robin de pádel solo puede ser dobles
        return ['doubles'];
      } else {
        // Liga escalera piramidal y sumatoria de juegos pueden ser singles, doubles o ambos
        return ['singles', 'doubles', 'ambos'];
      }
    }
  }
  
  // Tenis y Pickleball pueden tener cualquier modalidad para cualquier formato
  return ['singles', 'doubles', 'ambos'];
};

/**
 * Check if a modality is valid for a given sport, event type and format
 */
export const isModalityValid = (
  sport: Sport,
  eventType: EventType,
  modality: Modality,
  format?: TournamentFormat | LeagueFormat
): boolean => {
  const availableModalities = getAvailableModalities(sport, eventType, format);
  return availableModalities.includes(modality);
};

/**
 * Get subcategories based on sport and category
 */
export const getSubcategoriesForSport = (sport: Sport | string, category: Category | string): string[] => {
  // Normalize category to lowercase for comparison
  const normalizedCategory = category.toLowerCase();
  // Normalizar el deporte para manejar variantes como 'padel', 'Padel', 'Pádel'
  const normalizedSport = sport.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  if (normalizedSport === 'padel') {
    if (normalizedCategory.includes('mixta')) {
      return ['A', 'B', 'C', 'D'];
    } else if (normalizedCategory.includes('masculin') || normalizedCategory.includes('femenin')) {
      // Categorías masculina y femenina
      return ['1era', '2nda', '3era', '4ta', '5ta', '6ta'];
    }
  }
  
  // Tenis y Pickleball siempre tienen A, B, C, D
  return ['A', 'B', 'C', 'D'];
};

/**
 * Get all available categories for any sport
 */
export const getAllCategories = (): Category[] => {
  return ['masculina', 'femenina', 'mixta'];
};

/**
 * Get display name for modality
 */
export const getModalityDisplayName = (modality: Modality): string => {
  switch (modality) {
    case 'singles':
      return 'Individual';
    case 'doubles':
      return 'Dobles';
    case 'ambos':
      return 'Individual y Dobles';
    default:
      return modality;
  }
};

/**
 * Get display name for category
 */
export const getCategoryDisplayName = (category: Category): string => {
  switch (category) {
    case 'masculina':
      return 'Masculina';
    case 'femenina':
      return 'Femenina';
    case 'mixta':
      return 'Mixta';
    default:
      return category;
  }
};

/**
 * Get modality display text based on sport rules and match type
 */
export const getModalityDisplayForEvent = (
  sport: Sport,
  eventType: EventType,
  matchType: 'singles' | 'dobles' | 'ambos',
  format?: TournamentFormat | LeagueFormat
): string => {
  // For Pádel tournaments, always show "Dobles" regardless of matchType
  if (sport === 'Pádel' && eventType === 'tournament') {
    return 'Dobles';
  }
  
  // For all other cases, follow the matchType
  switch (matchType) {
    case 'singles':
      return 'Singles';
    case 'dobles':
      return 'Dobles';
    case 'ambos':
      return 'Singles y Dobles';
    default:
      return 'Dobles'; // Default fallback
  }
};